import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Clock, CalendarCheck, ListChecks, AlertTriangle, Users, Cake,
  Megaphone, Palmtree, ChevronRight, Sparkles, Sun, Moon, CloudSun, RefreshCcw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const greetingForHour = (h) => {
  if (h < 12) return { text: 'Good Morning', Icon: Sun };
  if (h < 17) return { text: 'Good Afternoon', Icon: CloudSun };
  return { text: 'Good Evening', Icon: Moon };
};

const ProgressRing = ({ pct, size = 52, stroke = 5 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={pct >= 100 ? '#93c5fd' : '#94a3b8'}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
    </svg>
  );
};

const DashboardDigest = () => {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const { getAuthHeaders, currentOffice, user } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchDigest(); }, [currentOffice]);

  // Re-fetch after check-in or planner close
  useEffect(() => {
    const handler = () => { setLoading(true); fetchDigest(); };
    window.addEventListener('checkin-complete', handler);
    return () => window.removeEventListener('checkin-complete', handler);
  }, []);

  const fetchDigest = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/employees/dashboard-digest`, { headers });
      if (res.ok) { setDigest(await res.json()); setTimeout(() => setVisible(true), 80); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading || !digest) return null;

  const { planned_activities, planned_total_minutes, meetings, tasks, birthdays, holidays, announcements } = digest;
  const hasPlanned = planned_activities.length > 0;
  const hasMeetings = meetings.length > 0;
  const hasBirthdays = birthdays.length > 0;
  const hasHolidays = holidays.length > 0;
  const hasAnnouncements = announcements.length > 0;
  const hasOverdue = tasks.overdue.length > 0;
  const hasDueToday = tasks.due_today.length > 0;
  const pctPlanned = Math.round((planned_total_minutes / 480) * 100);

  const formatMin = (m) => {
    if (!m) return '0h';
    const h = Math.floor(m / 60);
    const r = m % 60;
    return h > 0 ? `${h}h${r > 0 ? `${r}m` : ''}` : `${r}m`;
  };

  const now = new Date();
  const greeting = greetingForHour(now.getHours());
  const GIcon = greeting.Icon;
  const firstName = user?.full_name?.split(' ')[0] || '';

  const cardBase = 'rounded-2xl p-4 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md border border-blue-200 bg-blue-50/40';

  return (
    <div data-testid="dashboard-digest" className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 p-5 pb-4 mb-3">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />

        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GIcon className="h-5 w-5 text-slate-300" />
            <div>
              <p className="text-slate-400 text-[10px] uppercase tracking-widest mb-0.5">Today at a Glance</p>
              <h2 className="text-lg font-semibold text-white tracking-tight">{greeting.text}{firstName ? `, ${firstName}` : ''}</h2>
              <p className="text-slate-400 text-xs mt-0.5">
                {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm"
              className="text-slate-300 hover:text-white hover:bg-white/10 text-xs gap-1.5 rounded-xl"
              onClick={() => window.dispatchEvent(new CustomEvent('replan-day'))}
              data-testid="digest-replan-btn">
              <RefreshCcw className="h-3.5 w-3.5" /> Re-plan
            </Button>
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{formatMin(planned_total_minutes)}</p>
              <p className="text-slate-400 text-[10px] uppercase tracking-wider">of 8h planned</p>
            </div>
            <div className="relative">
              <ProgressRing pct={pctPlanned} />
              <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-semibold">
                {pctPlanned}%
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex flex-wrap gap-2 mt-3.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 text-slate-300 text-xs font-medium">
            <ListChecks className="h-3 w-3" /> {tasks.total_pending} tasks
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 text-slate-300 text-xs font-medium">
            <Users className="h-3 w-3" /> {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
          </span>
          {hasOverdue && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-slate-200 text-xs font-medium animate-pulse">
              <AlertTriangle className="h-3 w-3" /> {tasks.overdue.length} overdue
            </span>
          )}
          {hasBirthdays && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 text-slate-300 text-xs font-medium">
              <Cake className="h-3 w-3" /> Birthday!
            </span>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

        {/* Planned Activities */}
        <div className={cardBase} style={{ transitionDelay: '100ms' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-xl">
                <CalendarCheck className="h-4 w-4 text-blue-500" />
              </div>
              <span className="text-sm font-semibold text-gray-700">Planned</span>
            </div>
            {hasPlanned && <span className="text-blue-500 font-semibold text-base">{formatMin(planned_total_minutes)}</span>}
          </div>
          {hasPlanned ? (
            <div className="space-y-2">
              {planned_activities.slice(0, 3).map((a, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-300 shrink-0" />
                    <span className="text-sm text-gray-600 truncate">{a.task}</span>
                  </div>
                  <span className="text-xs text-blue-400 font-medium shrink-0 ml-2">{formatMin(a.minutes)}</span>
                </div>
              ))}
              {planned_activities.length > 3 && (
                <p className="text-[11px] text-blue-400">+{planned_activities.length - 3} more items</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 py-2">
              <Sparkles className="h-4 w-4 text-blue-200" />
              <p className="text-sm text-gray-400 italic">Start planning your day</p>
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full mt-3 h-8 text-xs text-blue-500 border-blue-200 hover:bg-blue-50 rounded-xl"
            onClick={() => navigate('/my-timesheet')} data-testid="digest-view-timesheet">
            View Timesheet <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>

        {/* Meetings */}
        <div className={cardBase} style={{ transitionDelay: '200ms' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Users className="h-4 w-4 text-blue-500" />
              </div>
              <span className="text-sm font-semibold text-gray-700">Meetings</span>
            </div>
            {hasMeetings && <span className="bg-blue-50 text-blue-500 font-semibold text-sm px-2.5 py-0.5 rounded-full">{meetings.length}</span>}
          </div>
          {hasMeetings ? (
            <div className="space-y-2">
              {meetings.slice(0, 3).map((m, i) => (
                <div key={m.id || i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 truncate flex-1 mr-2">{m.title}</span>
                  {m.time && (
                    <span className="text-xs bg-blue-50 text-blue-500 font-medium px-2 py-0.5 rounded-md shrink-0">{m.time}</span>
                  )}
                </div>
              ))}
              {meetings.length > 3 && (
                <p className="text-[11px] text-blue-400">+{meetings.length - 3} more</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-2">No meetings today</p>
          )}
        </div>

        {/* Pending Tasks */}
        <div className={cardBase} style={{ transitionDelay: '300ms' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-xl">
                <ListChecks className="h-4 w-4 text-blue-500" />
              </div>
              <span className="text-sm font-semibold text-gray-700">Tasks</span>
            </div>
            <span className="text-xl font-bold text-gray-700">{tasks.total_pending}</span>
          </div>
          <div className="space-y-2">
            {hasOverdue && (
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-50">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-blue-600 font-medium text-xs">{tasks.overdue.length} overdue</span>
              </div>
            )}
            {hasDueToday && (
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50">
                <Clock className="h-3 w-3 text-blue-400" />
                <span className="text-blue-500 font-medium text-xs">{tasks.due_today.length} due today</span>
              </div>
            )}
            {(tasks.overdue.length > 0 ? tasks.overdue : tasks.due_today).slice(0, 2).map((t, i) => (
              <div key={t.id || i} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 truncate flex-1 mr-2">{t.title}</span>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-blue-200 text-blue-400">{t.priority}</Badge>
              </div>
            ))}
            {!hasOverdue && !hasDueToday && tasks.total_pending === 0 && (
              <p className="text-sm text-gray-400 py-1">All caught up!</p>
            )}
          </div>
          <Button variant="outline" size="sm" className="w-full mt-3 h-8 text-xs text-blue-500 border-blue-200 hover:bg-blue-50 rounded-xl"
            onClick={() => navigate('/my-tasks')} data-testid="digest-view-tasks">
            View Tasks <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>

        {/* Announcements — always visible */}
        <div className={cardBase} style={{ transitionDelay: '400ms' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Megaphone className="h-4 w-4 text-blue-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">Announcements</span>
          </div>
          {hasAnnouncements ? (
            <div className="space-y-2">
              {announcements.map((a, i) => (
                <div key={a.id || i} className="flex items-center gap-2 cursor-pointer group" onClick={() => navigate('/newsroom')}>
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-300 shrink-0" />
                  <span className="text-sm text-gray-600 group-hover:text-blue-500 transition-colors line-clamp-1">{a.title}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-2">No announcements</p>
          )}
        </div>

        {/* Upcoming Holidays — always visible */}
        <div className={cardBase} style={{ transitionDelay: '500ms' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Palmtree className="h-4 w-4 text-blue-500" />
            </div>
            <span className="text-sm font-semibold text-gray-700">Upcoming Holidays</span>
          </div>
          {hasHolidays ? (
            <div className="space-y-2">
              {holidays.map((h, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 truncate flex-1 mr-2">{h.name}</span>
                  <span className="text-xs bg-blue-50 text-blue-500 font-medium px-2 py-0.5 rounded-md shrink-0">
                    {new Date(h.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-2">No upcoming holidays</p>
          )}
        </div>

        {/* Birthdays — always visible */}
        <div className={`${cardBase} relative overflow-hidden`} style={{ transitionDelay: '600ms' }}>
          {hasBirthdays && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-50/50 to-transparent -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" />
          )}
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Cake className="h-4 w-4 text-blue-500" />
              </div>
              <span className="text-sm font-semibold text-gray-700">Birthdays Today</span>
            </div>
            {hasBirthdays ? (
              <>
                <div className="space-y-2.5">
                  {birthdays.map((b, i) => (
                    <div key={b.id || i} className="flex items-center gap-3">
                      {b.profile_picture ? (
                        <img src={b.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover border border-blue-200 shadow-sm" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 text-sm font-semibold">
                          {b.name?.[0]}
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-700">{b.name}</span>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="w-full mt-3 h-8 text-xs text-blue-500 border-blue-200 hover:bg-blue-50 rounded-xl"
                  onClick={() => navigate('/ping')} data-testid="digest-wish-birthday">
                  Send a wish <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </>
            ) : (
              <p className="text-sm text-gray-400 italic py-2">No birthdays today</p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          50%, 100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default DashboardDigest;
